import { Map } from 'immutable';
import { v4 as uuidV4 } from 'uuid';
import { reducerWithInitialState } from 'typescript-fsa-reducers/dist';
import {
  ActivityStart,
  EvaluatedResponse,
  ContentSetup,
  ContentType,
  ProgressLoaded,
  ScoredOption,
  ModuleInfo
} from '../../utils';
import { activityStart } from '../actions/embedding';
import {
  nextProblemChosen,
  progressionEndReached,
  progressStateLoaded,
  responseEvaluated,
  incrementCorrectAnswer,
  resetMastery
} from '../actions/progression';
import {
  problemSetupFingerprint,
  problemSetupToString
} from '../selectors/progression';

export interface ProgressionState {
  sessionId: string;
  activityId: string;
  cid: string;
  moduleInfo: ModuleInfo;
  completed: boolean;
  isResumed: boolean;
  problemsDone: Map<string, ScoredOption[]>;
  startType: ContentType;
  masteryRollingAverageArray: number[];
  masteryRollingAverage: number;
  correctAnswerStreak: number; // negative means incorrect
  noLogging: boolean;
  /** Current progress % (0 - 100) */
  progress: number;
  /** Total time spent so far in milliseconds */
  cumulativeTime: number;
}

const INITIAL_ROLLING_AVERAGE_ARRAY = [0, 0, 0, 0];

const INITIAL_STATE: ProgressionState = {
  sessionId: '',
  activityId: '',
  cid: '',
  moduleInfo: {
    skill: { name: '', id: '', content: { text: '' } },
    curriculum: { name: '', version: '' },
    progression: []
  },
  completed: false,
  isResumed: false,
  problemsDone: Map(),
  startType: ContentType.ModuleIntro,
  masteryRollingAverageArray: INITIAL_ROLLING_AVERAGE_ARRAY,
  masteryRollingAverage: 0,
  correctAnswerStreak: 0,
  noLogging: false,
  progress: 0,
  cumulativeTime: 0,
};

const nextProblemChosenReducer = (
  state: ProgressionState
): ProgressionState => ({ ...state });

const activityStartReducer = (
  state: ProgressionState,
  activityStart: ActivityStart
): ProgressionState => {
  const sessionId = uuidV4();
  const { cid, moduleInfo, activityId, noLogging } = activityStart;
  return {
    ...state,
    cid,
    moduleInfo,
    activityId,
    sessionId,
    startType: ContentType.ModuleIntro,
    noLogging,
  };
};

const responseEvaluatedReducer = (
  state: ProgressionState,
  { setup, score, validOptions, progress, responseTime }: EvaluatedResponse
): ProgressionState => {
  if (setup === undefined) {
    return state;
  }

  const { problemsDone } = state;
  const fingerprint = problemSetupToString(setup);
  const current = problemsDone.get(fingerprint) || [];
  current.push({ score, validOptions });
  const cumulativeTime = (state.cumulativeTime ?? 0) + responseTime;

  return {
    ...state,
    progress,
    cumulativeTime,
    problemsDone: problemsDone.set(fingerprint, current)
  };
};

const progressionEndReachedReducer = (
  state: ProgressionState,
  completed: boolean
): ProgressionState => ({ ...state, completed, progress: 100 });

const progressStateLoadedReducer = (
  state: ProgressionState,
  savedState: ProgressLoaded
): ProgressionState => {
  const {
    moduleState: {
      problemSetupState,
      problemSetupState: { progressionIndex, difficulty },
      progression
    }
  } = savedState;

  if (!problemSetupState) {
    return state;
  }

  const { moduleInfo } = state;
  const savedSkillProgression = progression;
  const { activityId, sessionId } = state;

  const isResumed =
    savedSkillProgression !== undefined &&
    activityId === savedSkillProgression.activityId;

  if (isResumed) {
    const problemsDone = Map(savedSkillProgression.problemsDone);
    const fingerprint = problemSetupFingerprint(
      moduleInfo,
      difficulty,
      progressionIndex
    );
    const current = problemsDone.get(fingerprint) || [];
    const last4Problems = current.slice(-4);

    const masteryRollingAverageArray = last4Problems.map((v): number => {
      return v.score > 0.95 ? 1 : 0;
    });

    for (
      let i =
        INITIAL_ROLLING_AVERAGE_ARRAY.length -
        masteryRollingAverageArray.length;
      i;
      i--
    ) {
      masteryRollingAverageArray.unshift(0);
    }

    const masteryRollingAverage = masteryRollingAverageArray.reduce(
      (a, c): number => {
        return a + c / masteryRollingAverageArray.length;
      },
      0
    );

    return {
      ...savedSkillProgression,
      sessionId,
      activityId,
      isResumed,
      problemsDone,
      masteryRollingAverage,
      masteryRollingAverageArray
    };
  } else {
    return {
      ...state,
      isResumed,
      sessionId,
      activityId
    };
  }
};

function incrementCorrectAnswerReducer(
  state: ProgressionState,
  increment: number
): ProgressionState {
  const correctAnswerStreak =
    increment > 0
      ? state.correctAnswerStreak > 0
        ? state.correctAnswerStreak + 1
        : 1
      : state.correctAnswerStreak < 0
      ? state.correctAnswerStreak - 1
      : -1;
  const masteryRollingAverageArray = state.masteryRollingAverageArray.slice(1);
  masteryRollingAverageArray.push(increment > 0 ? 1 : 0);

  const masteryRollingAverage =
    masteryRollingAverageArray.reduce((a, v): number => a + v) /
    masteryRollingAverageArray.length;

  return {
    ...state,
    correctAnswerStreak,
    masteryRollingAverageArray,
    masteryRollingAverage
  };
}

function resetMasteryReducer(state: ProgressionState): ProgressionState {
  return {
    ...state,
    masteryRollingAverage: 0,
    masteryRollingAverageArray: INITIAL_ROLLING_AVERAGE_ARRAY
  };
}

export const progressionReducers = reducerWithInitialState(INITIAL_STATE)
  .case(activityStart, activityStartReducer)
  .case(progressionEndReached, progressionEndReachedReducer)
  .case(nextProblemChosen, nextProblemChosenReducer)
  .case(progressStateLoaded, progressStateLoadedReducer)
  .case(incrementCorrectAnswer, incrementCorrectAnswerReducer)
  .case(resetMastery, resetMasteryReducer)
  .case(responseEvaluated, responseEvaluatedReducer);

export const getResponses = (
  state: ProgressionState,
  setup: ContentSetup
): ScoredOption[] | undefined => {
  const { problemsDone } = state;
  const key = problemSetupToString(setup);
  return problemsDone.get(key);
};
